'use client';

import { motion } from 'motion/react';
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@lib/utils';

import { FLIP_RETURN_TRANSITION, FLIP_TRANSITION } from './constants';
import { useDeferredFlip } from './useDeferredFlip.hook';

// 카드 3D 플립 셸(순수 표현). 뷰포트별 크기는 반응형(모바일 280×448 → 데스크탑 360×488).
// 플립 여부·접근성 게이팅(inert/aria-hidden)은 상위(DesktopCard/MobileCard)가 계산해 내려준다.
export function FlipCard({
  flipped,
  interactive = true,
  className,
  outer,
  front,
  back,
  frontHidden,
  backHidden,
}: {
  flipped: boolean;
  // 중앙 근처(상호작용 가능) 카드 여부. false(화면 밖)면 inert 로 포커스·SR·포인터에서 제외.
  interactive?: boolean;
  className?: string;
  // 바깥 컨테이너에 붙는 이벤트 핸들러(데스크탑 hover/focus/click). 모바일은 미전달.
  outer?: HTMLAttributes<HTMLDivElement>;
  front: ReactNode;
  back: ReactNode;
  // 화면에 안 보이는 면을 aria-hidden 으로 SR 에서 제외(모바일만 사용, 데스크탑은 포커스 시 플립되어 항상 도달)
  frontHidden?: boolean;
  backHidden?: boolean;
}) {
  // 진행 중인 플립은 완주시킨 뒤 최신 목표로 반영(회전 중 되돌아옴 방지)
  const { flip, onComplete } = useDeferredFlip(flipped);
  return (
    <div
      className={cn(
        'h-[448px] w-[280px] [perspective:1600px] md:h-[488px] md:w-[360px]',
        className,
      )}
      inert={!interactive}
      {...outer}
    >
      <motion.div
        className="relative h-full w-full"
        style={{ transformStyle: 'preserve-3d' }}
        initial={false}
        animate={{ rotateY: flip ? 180 : 0 }}
        transition={flip ? FLIP_TRANSITION : FLIP_RETURN_TRANSITION}
        onAnimationComplete={onComplete}
      >
        <div
          className="absolute inset-0"
          style={{ backfaceVisibility: 'hidden' }}
          aria-hidden={frontHidden || undefined}
        >
          {front}
        </div>

        {/* 뒷면 — 중심축 기준 rotateY(180) (제자리 뒤집힘) */}
        <div
          className="absolute inset-0"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          aria-hidden={backHidden || undefined}
        >
          {back}
        </div>
      </motion.div>
    </div>
  );
}
